/* =====================================================================
   SEO — résolution des métadonnées + données structurées (JSON-LD)
   pour n'importe quel chemin de l'application.
===================================================================== */
import {
  SITE_URL,
  SITE_NAME,
  DEFAULT_IMAGE,
  DEFAULT_TITLE,
  DEFAULT_DESCRIPTION,
  STATIC_ROUTES,
  PROJECT_COLLECTIONS,
  normalizePath,
  projectSeo,
  cleanText,
} from "./routes";
import * as CollectionsData from "../Pages/Collections/CollectionsData";

/* ---------- Images de couverture (URL absolues pour og:image) ---------- */
const coverContexts = {
  Cuisines: require.context("../images/Cuisines", true, /-couverture\.jpg$/),
  Dressings: require.context("../images/Dressings", true, /-couverture\.jpg$/),
  ProjetsInter: require.context("../images/ProjetsInter", true, /-couverture\.jpg$/),
};

const toAbsolute = (src) => {
  if (!src || typeof src !== "string" || src.startsWith("data:")) return null;
  if (/^https?:\/\//.test(src)) return src;
  return `${SITE_URL}${src.startsWith("/") ? "" : "/"}${src}`;
};

const getCover = (folder, key) => {
  try {
    const mod = coverContexts[folder](`./${key}/${key}-couverture.jpg`);
    return toAbsolute(mod?.default || mod);
  } catch {
    return null;
  }
};

/* ---------- Entités réutilisées ---------- */
export const ORG_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;

export const organizationLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": ORG_ID,
  name: SITE_NAME,
  alternateName: ["Elements", "Elements Tunisie"],
  url: `${SITE_URL}/`,
  logo: `${SITE_URL}/logo512.png`,
  image: DEFAULT_IMAGE,
  description: DEFAULT_DESCRIPTION,
  email: "Mtr@elements.tn",
  telephone: "+216 24 870 001",
  areaServed: ["TN", "FR", "Worldwide"],
  knowsAbout: [
    "Cuisine sur mesure",
    "Cuisine haut de gamme",
    "Dressing sur mesure",
    "Aménagement intérieur",
    "Design intérieur",
  ],
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: "+216 24 870 001",
      contactType: "customer service",
      areaServed: "TN",
      availableLanguage: ["French", "Arabic"],
    },
    {
      "@type": "ContactPoint",
      telephone: "+33 6 33 80 95 55",
      contactType: "sales",
      areaServed: "FR",
      availableLanguage: ["French"],
    },
  ],
  sameAs: [
    "https://www.instagram.com/elements.tunisie",
    "https://www.facebook.com/Elementstn",
    "https://www.linkedin.com/company/elements-tunisie/",
  ],
};

const websiteLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  url: `${SITE_URL}/`,
  name: SITE_NAME,
  inLanguage: "fr-FR",
  publisher: { "@id": ORG_ID },
};

const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path === "/" ? "/" : it.path}`,
  })),
});

const webPageLd = ({ path, title, description, type = "WebPage", extra = {} }) => ({
  "@context": "https://schema.org",
  "@type": type,
  "@id": `${SITE_URL}${path}#webpage`,
  url: `${SITE_URL}${path}`,
  name: title,
  description,
  inLanguage: "fr-FR",
  isPartOf: { "@id": WEBSITE_ID },
  publisher: { "@id": ORG_ID },
  ...extra,
});

/* ---------- Index des routes ---------- */
const staticMap = Object.fromEntries(STATIC_ROUTES.map((r) => [r.path, r]));

const collectionByBase = Object.fromEntries(
  PROJECT_COLLECTIONS.map((c) => [c.basePath, c])
);

const projectItemList = (collection) => {
  const data = CollectionsData[collection.exportName] || {};
  return {
    "@type": "ItemList",
    numberOfItems: Object.keys(data).length,
    itemListElement: Object.entries(data).map(([key, p], i) => {
      const s = projectSeo(collection, key, p);
      return {
        "@type": "ListItem",
        position: i + 1,
        url: `${SITE_URL}${s.path}`,
        name: s.label,
      };
    }),
  };
};

/* ---------------------------------------------------------------------
   getSeoForPath(pathname) → props pour <SEO />
--------------------------------------------------------------------- */
export function getSeoForPath(pathname) {
  const path = normalizePath(pathname);
  const home = { name: "Accueil", path: "/" };

  /* 1. Pages statiques */
  const st = staticMap[path];
  if (st) {
    const title = st.title || DEFAULT_TITLE;
    const description = st.description || DEFAULT_DESCRIPTION;

    if (path === "/") {
      return {
        path,
        title,
        description,
        jsonLd: [organizationLd, websiteLd, webPageLd({ path, title, description })],
      };
    }

    const collection = collectionByBase[path];
    const jsonLd = [
      webPageLd({
        path,
        title,
        description,
        type: collection ? "CollectionPage" : path === "/contact" ? "ContactPage" : "WebPage",
        extra: collection ? { mainEntity: projectItemList(collection) } : {},
      }),
      breadcrumbLd([home, { name: st.label || title, path }]),
    ];
    if (path === "/contact") jsonLd.unshift(organizationLd);

    return { path, title, description, noindex: !st.indexable, jsonLd };
  }

  /* 2. Pages projet dynamiques */
  const base = path.substring(0, path.lastIndexOf("/"));
  const key = decodeURIComponent(path.substring(path.lastIndexOf("/") + 1));
  const collection = collectionByBase[base];
  const project = collection && CollectionsData[collection.exportName]?.[key];

  if (project) {
    const s = projectSeo(collection, key, project);
    const image = getCover(collection.imageFolder, key) || DEFAULT_IMAGE;
    const finishes = (project.finishes || []).map((f) => cleanText(f.name));

    return {
      path,
      title: s.title,
      description: s.description,
      image,
      imageAlt: `${collection.kind} – ${s.place} – ${SITE_NAME}`,
      type: "article",
      jsonLd: [
        webPageLd({
          path,
          title: s.title,
          description: s.description,
          extra: { primaryImageOfPage: image },
        }),
        {
          "@context": "https://schema.org",
          "@type": "CreativeWork",
          "@id": `${SITE_URL}${path}#project`,
          name: `${collection.kind} – ${s.label}`,
          headline: s.title.replace(` | ${SITE_NAME}`, ""),
          description: cleanText(project.description),
          url: `${SITE_URL}${path}`,
          image,
          inLanguage: "fr-FR",
          genre: collection.kind,
          keywords: [collection.kind, s.place, ...finishes].join(", "),
          material: finishes,
          locationCreated: { "@type": "Place", name: s.place },
          creator: { "@id": ORG_ID },
          publisher: { "@id": ORG_ID },
          mainEntityOfPage: { "@id": `${SITE_URL}${path}#webpage` },
        },
        breadcrumbLd([
          home,
          { name: collection.parentLabel, path: collection.basePath },
          { name: s.label, path },
        ]),
      ],
    };
  }

  /* 3. Introuvable → noindex */
  return {
    path,
    title: `Page introuvable | ${SITE_NAME}`,
    description: DEFAULT_DESCRIPTION,
    noindex: true,
    notFound: true,
  };
}
